import { t } from '@lingui/core/macro';
import {
  Checkbox,
  FormControl,
  FormHelperText,
  type FormHelperTextProps,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  type SelectProps,
  styled,
} from '@mui/material';
import { isNil } from 'lodash';
import { type ReactNode, useState } from 'react';
import type { FilterItem } from 'types/filter';
import { ClearIcon } from './icons/ClearIcon';

export type SelectValue<
  Value extends string | number,
  Multiple extends boolean,
> = Multiple extends true ? Array<Value> : Value;

export type SelectFieldProps<Value extends string | number, Multiple extends boolean = false> = {
  multiple?: Multiple;
  helperText?: ReactNode;
  helperTextSx?: FormHelperTextProps['sx'];
  options: FilterItem<Value>[];
  required?: boolean;
  sx?: SelectProps['sx'];
  label?: string;
  onChange?: (value: SelectValue<Value, Multiple>) => void;
  onClear?: () => void;
} & Omit<
  SelectProps<SelectValue<Value, Multiple>>,
  'multiple' | 'label' | 'helperText' | 'options' | 'required' | 'sx' | 'onChange' | 'onClear'
>;

const SelectField = <Value extends string | number, Multiple extends boolean = false>(
  props: SelectFieldProps<Value, Multiple>
) => {
  const [generatedId] = useState(Math.random().toString(36).substring(2, 15));
  const {
    id = generatedId,
    label,
    helperText,
    helperTextSx,
    options,
    required,
    sx,
    value,
    renderValue,
    onClear,
    onChange,
    ...rest
  } = props;

  const defaultRenderValue = (value: SelectValue<Value, Multiple>) =>
    Array.isArray(value)
      ? value?.map((v) => options.find((o) => o.value === v)?.title).join(', ')
      : options.find((o) => o.value === value)?.title;

  return (
    <FormControl fullWidth sx={sx} required={required}>
      {label && (
        <InputLabel id={`label-${id}`} htmlFor={id}>
          {label}
        </InputLabel>
      )}
      <Select
        id={id}
        aria-describedby={`help-${id}`}
        label={label}
        labelId={label ? `label-${id}` : undefined}
        value={props.value ?? ((props.multiple ? [] : '') as SelectValue<Value, Multiple>)}
        MenuProps={{
          sx: { '& .MuiPaper-root': { maxHeight: 260 } },
        }}
        renderValue={renderValue || defaultRenderValue}
        {...(onClear && (Array.isArray(value) ? value.length > 0 : !isNil(value))
          ? {
              endAdornment: (
                <IconButton
                  onClick={onClear}
                  size="small"
                  sx={{ marginRight: '7px' }}
                  disabled={props.disabled}
                  aria-label={label ? t`Effacer la sélection de ${label}` : t`Effacer la sélection`}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }
          : {})}
        onChange={(e) => onChange?.(e.target.value as SelectValue<Value, Multiple>)}
        {...rest}
      >
        {options.map((option) => {
          const checked = Array.isArray(value) && value?.includes(option.value);
          return (
            <StyledMenuItem
              key={option.value}
              value={option.value}
              sx={{ typography: 'body1', ...option.sx }}
              disabled={option.isDisabled}
              disableRipple
            >
              {props.multiple && <Checkbox size="small" checked={checked} />}
              {option.title}
            </StyledMenuItem>
          );
        })}
      </Select>
      {helperText && (
        <FormHelperText id={`help-${id}`} sx={helperTextSx}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  margin: '4px 8px',
  borderRadius: theme.shape.borderRadius,

  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.lighter,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },

  '.MuiCheckbox-root': {
    marginLeft: '-8px',
  },
}));

export default SelectField;
